import React from 'react';
import { fmtMoney, fmtNum } from './chartTheme';
import { formatClinicDate, formatClinicDateTime } from '../../../utils/clinicTime';

// The pieces the profile reports (Patient Profile, Medicine Profile) build their tables from: one
// column builder that gives the screen, the CSV and the printout the same words for a figure, the
// status badge, and the formatters behind them.

export const DASH = '—';

/** "IN_CONSULTATION" -> "In Consultation". Leaves anything that is not an enum-style word alone. */
export const human = (v) => {
  if (v === null || v === undefined || v === '') return DASH;
  const s = String(v);
  if (!/^[A-Z0-9_]+$/.test(s)) return s;
  return s
    .toLowerCase()
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
};

export const fmtDate = (v) => formatClinicDate(v, { day: 'numeric', month: 'short', year: 'numeric' }, DASH);
export const fmtDateTime = (v) => formatClinicDateTime(v, {}, DASH);

/** Money with its sign spelled out, for ledgers where direction is the point. */
export const fmtSigned = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return DASH;
  if (n === 0) return fmtMoney(0);
  return `${n > 0 ? '+' : '-'}${fmtMoney(Math.abs(n))}`;
};

/** A count with its sign, for stock movements: "+60", "-9". */
export const fmtSignedNum = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return DASH;
  return n > 0 ? `+${fmtNum(n)}` : fmtNum(n);
};

/** A percentage to one decimal, signed when asked: "+20.5%". */
export const fmtPercent = (v, signed = false) => {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return DASH;
  const s = `${n.toLocaleString('en-PK', { maximumFractionDigits: 1 })}%`;
  return signed && n > 0 ? `+${s}` : s;
};

export const yesNo = (v) => (v ? 'Yes' : 'No');

/** File size for a document row. */
export const fmtSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return DASH;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ---------------------------------------------------------------- statuses

const TONES = {
  green: 'bg-emerald-100 text-emerald-800',
  red: 'bg-rose-100 text-rose-800',
  amber: 'bg-amber-100 text-amber-800',
  violet: 'bg-violet-100 text-violet-800',
  blue: 'bg-sky-100 text-sky-800',
  grey: 'bg-gray-100 text-gray-700',
};

const STATUS_TONE = {
  // visits
  COMPLETED: 'green',
  CANCELLED: 'red',
  REFERRED: 'violet',
  IN_CONSULTATION: 'blue',
  CALLED: 'blue',
  READY_FOR_DOCTOR: 'amber',
  PENDING_VITALS: 'amber',
  // prescriptions
  INITIATED: 'grey',
  DRAFT: 'amber',
  PRESCRIBED: 'blue',
  INVOICED: 'amber',
  ISSUED: 'green',
  // invoices and receipts
  PAID: 'green',
  FINALIZED: 'amber',
  ACTIVE: 'green',
  // patients
  DECEASED: 'grey',
  SUSPENDED: 'amber',
  BLACKLISTED: 'red',
  // alerts
  CRITICAL: 'red',
  HIGH: 'red',
  MEDIUM: 'amber',
  LOW: 'grey',
  // stock
  IN_STOCK: 'green',
  LOW_STOCK: 'amber',
  OUT_OF_STOCK: 'red',
  NEAR_EXPIRY: 'amber',
  EXPIRED: 'red',
};

export const StatusBadge = ({ value, label, tone }) => {
  if (!value && !label) return DASH;
  const cls = TONES[tone || STATUS_TONE[value] || 'grey'];
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>
      {label || human(value)}
    </span>
  );
};

// ---------------------------------------------------------------- columns

const RIGHT = new Set(['money', 'number', 'signed', 'signedNum', 'percent']);

/** The text of a cell, as the screen and the printout both show it. */
export const fmtCell = (type, v) => {
  if (v === null || v === undefined || v === '') return DASH;
  switch (type) {
    case 'money':
      return fmtMoney(v);
    case 'signed':
      return fmtSigned(v);
    case 'signedNum':
      return fmtSignedNum(v);
    case 'percent':
      return fmtPercent(v, true);
    case 'number':
      return fmtNum(v);
    case 'date':
      return fmtDate(v);
    case 'datetime':
      return fmtDateTime(v);
    case 'status':
      return human(v);
    case 'bool':
      return yesNo(v);
    default:
      return String(v);
  }
};

const NUMERIC = new Set(['money', 'signed', 'signedNum', 'percent', 'number']);

/** What goes in the CSV: raw numbers for money, so a spreadsheet can add them up. */
const csvCell = (type, v) => {
  if (v === null || v === undefined) return '';
  if (NUMERIC.has(type)) return v;
  if (type === 'date' || type === 'datetime' || type === 'status' || type === 'bool') return fmtCell(type, v);
  return v;
};

/**
 * One table column, for ReportTable. `extra` may override render (screen) and text (a plain-string
 * value used for CSV and print when the screen render is JSX).
 */
export const col = (key, label, type = 'text', extra = {}) => {
  const { text, ...rest } = extra;
  const value = (r) => (typeof key === 'function' ? key(r) : r[key]);
  const k = typeof key === 'function' ? label : key;
  return {
    key: k,
    label,
    align: RIGHT.has(type) ? 'right' : undefined,
    // 'wrap' is free text (a diagnosis, a note) allowed to break across lines; see ReportTable.
    wrap: type === 'wrap' ? true : undefined,
    render: (r) => {
      if (type === 'status') return <StatusBadge value={value(r)} />;
      if (type === 'mono') return <span className="font-mono text-xs">{fmtCell(type, value(r))}</span>;
      return text ? text(r) : fmtCell(type, value(r));
    },
    csv: (r) => (text ? text(r) : csvCell(type, value(r))),
    print: (r) => (text ? text(r) : fmtCell(type, value(r))),
    // What the table's search box matches: the text on screen, since a computed column has no
    // property of its own on the row.
    searchText: (r) => (text ? text(r) : fmtCell(type, value(r))),
    sortValue: (r) => {
      const v = value(r);
      if (NUMERIC.has(type)) return v === null || v === undefined ? null : Number(v);
      return v;
    },
    ...rest,
  };
};

/** The same columns, shaped for ReportPrintLayout (which prints `csv`, so print text goes there). */
export const forPrint = (columns) =>
  columns.map((c) => ({ key: c.key, label: c.label, align: c.align, csv: c.print || c.csv }));
